import { FabricEditor, FabricEditorCanvas, FabricEditorProvider } from 'modules/fabric-editor';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import React, { useEffect, useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { RENDER_PLUGINS, loadCanvasJson, nextFrame } from '../pages/render-shared';

interface Props {
  template: MediaManagerTemplate;
  /** Longest-side target size of the generated thumbnail, in px. */
  targetPx?: number;
}

/**
 * Renders a template's authored canvas off-screen (same headless technique as the
 * render pages) and shows a downscaled PNG thumbnail. No placeholder substitution —
 * the preview reflects the template as authored (placeholder cues included).
 */
const MediaManagerPresetPreview: React.FC<Props> = ({ template, targetPx = 480 }) => {
  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const captured = useRef(false);

  const isEmpty = !template.objects || template.objects.length === 0;

  useEffect(() => {
    if (isEmpty || !editor || captured.current) return;
    captured.current = true;

    (async () => {
      try {
        await loadCanvasJson(editor, template.version, template.objects ?? []);
        // Best-effort font fidelity before snapshot; ignore if unsupported.
        if (typeof document !== 'undefined' && document.fonts?.ready) {
          await document.fonts.ready;
        }
        await nextFrame();
        const multiplier = Math.min(1, targetPx / Math.max(template.width, template.height));
        setDataUrl(editor.canvas.toDataURL({ format: 'png', multiplier }));
      } catch (error) {
        console.error('Failed to render preset preview:', error);
        setFailed(true);
      }
    })();
  }, [editor, isEmpty, targetPx, template]);

  return (
    <div className="bg-BorderGray50 relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200">
      {isEmpty ? (
        <span className="text-sm text-gray-400">Пустой шаблон</span>
      ) : failed ? (
        <span className="text-sm text-gray-400">Не удалось построить превью</span>
      ) : dataUrl ? (
        <img src={dataUrl} alt={template.name} className="h-full w-full object-contain" />
      ) : (
        <ClipLoader size={28} />
      )}

      {/* Off-screen editor: mounted only until the thumbnail is captured. */}
      {!isEmpty && !dataUrl && !failed && (
        <div style={{ position: 'fixed', left: -99999, top: -99999, pointerEvents: 'none' }} aria-hidden>
          <FabricEditorProvider editor={editor}>
            <FabricEditorCanvas
              key={`${template.id}-${template.width}x${template.height}`}
              width={template.width}
              height={template.height}
              resolution={{ width: template.width, height: template.height }}
              plugins={RENDER_PLUGINS}
              onReady={setEditor}
            />
          </FabricEditorProvider>
        </div>
      )}
    </div>
  );
};

export default React.memo(MediaManagerPresetPreview);
